import { useContext, useMemo } from 'react';
import { Loader } from '../Common/Loader';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum } from '@/types/common';
import { PaymentMethodsToShowLoaderWhilePolling, PaymentMethodsToShowLoaderWhileProcessing } from '@/constants/payment';
import { shouldShowLoader } from '@/utils/conditionalRendering';
import { stripeInPageNextActionPaymentMethods } from '@/constants/stripe';
import { isMobileDevice } from '@/utils/device';
import { PaymentProcessOverlay } from '../Common/PaymentProcessOverlay';

export const ProcessingLoader = () => {
  const {
    payment: { isInPaymentProcess, isExpressPaymentInProcess, isApplePayTabOpen },
    paymentMethodSelected,
    otherMethodSelected,
    featureFlags,
    threeDSSecurityState: { show3DSForm },
    redirectPayment,
    successScreen,
  } = useContext(CheckoutContext);

  const selectedMethod = useMemo(() => {
    return paymentMethodSelected === PaymentMethodEnum.OTHER ? otherMethodSelected : paymentMethodSelected;
  }, [paymentMethodSelected, otherMethodSelected]);

  const showLoaderWhileProcessing =
    isInPaymentProcess && PaymentMethodsToShowLoaderWhileProcessing.includes(selectedMethod as PaymentMethodEnum);
  const showLoaderWhilePolling =
    isInPaymentProcess &&
    Boolean(successScreen?.isOpen) &&
    PaymentMethodsToShowLoaderWhilePolling.includes(selectedMethod as PaymentMethodEnum);
  const redirectOverlayIsActive = useMemo(() => {
    const usesStripeInPageNextActionOnDesktop =
      !!selectedMethod &&
      stripeInPageNextActionPaymentMethods.includes(selectedMethod as PaymentMethodEnum) &&
      !isMobileDevice();

    return Boolean(
      (redirectPayment.redirectUrl || redirectPayment.showRedirect) &&
      selectedMethod !== PaymentMethodEnum.PIX &&
      !usesStripeInPageNextActionOnDesktop
    );
  }, [redirectPayment.redirectUrl, redirectPayment.showRedirect, selectedMethod]);

  if (!shouldShowLoader(featureFlags)) {
    return null;
  }

  // The 3DS challenge (Adyen modal / Stripe iframe) renders at z-150, same as this
  // loader. Methods that show this loader while processing keep their in-process flag
  // true through the 3DS chain, so without this gate the loader would cover the
  // challenge and block authentication. handlePaymentSuccess/handlePaymentError reset
  // `show3DSForm` afterwards so the loader returns for the finalize + order-polling stage.
  if (show3DSForm) {
    return null;
  }

  if (redirectOverlayIsActive) {
    return null;
  }

  // Apple Pay fallback tab: the payment is happening in another window, so a
  // bare spinner reads as a hung checkout. Same overlay the PSP redirect tab
  // uses, minus the cancel button — the shopper cancels by closing the tab,
  // which `useApplePayTabHandoff` already picks up.
  if (isApplePayTabOpen) {
    return <PaymentProcessOverlay />;
  }

  if (showLoaderWhileProcessing || showLoaderWhilePolling || isExpressPaymentInProcess) {
    return <ProcessingLoaderOverlay />;
  }
  return null;
};

/** The bare full-screen spinner: dimmed backdrop, no card, no copy. */
export const ProcessingLoaderOverlay = () => (
  <div
    data-testid="processing-loader"
    className="fixed inset-0 bg-base-black/50 flex items-center justify-center z-150"
    role="status"
    aria-live="polite"
  >
    <Loader size="lg" variant="checkout" />
  </div>
);
